import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterPanel, SelectFilter, DateRangePicker } from '@/components/FilterPanel'
import { SearchInput } from '@/components/SearchInput'
import { DocumentDetailDrawer } from '@/components/DocumentDetailDrawer'
import { relativeTime, exactTimestamp } from '@/utils/time'
import { useActivity, useOrganizations } from '@/hooks/useRootQueries'
import type { AuditEvent } from '@/types/api'

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'expired', label: 'Expired' },
  { value: 'signed', label: 'Signed' },
  { value: 'created', label: 'Created' },
  { value: 'sent', label: 'Sent' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
  { value: 'voided', label: 'Voided' },
]

const POLL_OPTIONS = [
  { value: '0', label: 'Manual' },
  { value: '5000', label: '5s' },
  { value: '15000', label: '15s' },
  { value: '30000', label: '30s' },
]

// Normalizes audit strings into concise enterprise tags
function formatActionTag(rawAction: string): { label: string; style: string; detail?: string } {
  const clean = (rawAction || '').trim()
  const lower = clean.toLowerCase()

  if (lower.includes('expire')) {
    return {
      label: 'Expired',
      style: 'bg-amber-50 text-amber-800 border-amber-300 font-semibold',
      detail: clean !== 'Expired' ? clean : undefined,
    }
  }
  if (lower.includes('complete') || lower.includes('all signed') || lower.includes('finished')) {
    return {
      label: 'Completed',
      style: 'bg-emerald-100 text-emerald-900 border-emerald-400 font-bold',
      detail: clean,
    }
  }
  if (lower.includes('signed') || lower.startsWith('sign')) {
    return {
      label: 'Signed',
      style: 'bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold',
      detail: clean,
    }
  }
  if (lower.includes('viewed') || lower.includes('opened') || lower.includes('read')) {
    return {
      label: 'Viewed',
      style: 'bg-indigo-50 text-indigo-800 border-indigo-300 font-semibold',
      detail: clean,
    }
  }
  if (lower.includes('sent') || lower.includes('invited') || lower.includes('invite') || lower.includes('mail')) {
    return {
      label: 'Sent',
      style: 'bg-sky-50 text-sky-800 border-sky-300 font-semibold',
      detail: clean,
    }
  }
  if (lower.includes('create') || lower.includes('upload') || lower.includes('draft')) {
    return {
      label: 'Created',
      style: 'bg-slate-100 text-slate-800 border-slate-300 font-semibold',
      detail: clean,
    }
  }
  if (lower.includes('otp') || lower.includes('auth') || lower.includes('verify') || lower.includes('consent')) {
    return {
      label: 'Verified',
      style: 'bg-teal-50 text-teal-800 border-teal-300 font-semibold',
      detail: clean,
    }
  }
  if (lower.includes('void') || lower.includes('decline') || lower.includes('fail') || lower.includes('cancel') || lower.includes('reject')) {
    return {
      label: lower.includes('decline') || lower.includes('reject') ? 'Declined' : 'Voided',
      style: 'bg-rose-50 text-rose-800 border-rose-300 font-semibold',
      detail: clean,
    }
  }
  return {
    label: clean.replace(/^Document\s+/i, '').slice(0, 20) || 'Activity',
    style: 'bg-slate-50 text-slate-700 border-slate-200 font-medium',
    detail: clean,
  }
}

// Extracts actual actor/signer name and email with intelligent fallbacks
function getEffectiveActor(row: AuditEvent): { name: string; email?: string } {
  let name = row.actorName || row.actor_name || row.actor || ''
  const email = row.actorEmail || row.actor_email || ''

  if (!name || name.toLowerCase() === 'system') {
    const rawAction = row.action || ''
    const byMatch = rawAction.match(/(?:signed by|viewed by|created by|sent to|assigned to|declined by|invited)\s+([^,.(]+)/i)
    if (byMatch && byMatch[1]) {
      name = byMatch[1].trim()
    } else if (email) {
      name = email.split('@')[0]
    } else {
      name = 'System'
    }
  }

  return { name, email }
}

// Parses and formats multi-line or comma-separated IP lists cleanly
function formatIPs(rawIp?: string, actor?: string) {
  if (!rawIp) {
    if (actor && actor.toLowerCase().includes('system')) {
      return (
        <span className="inline-flex items-center gap-1 rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
          <span>⚙️</span>
          <span>System Job</span>
        </span>
      )
    }
    return <span className="text-slate-400 font-mono text-xs">—</span>
  }
  const parts = rawIp.split(',').map((s) => s.trim()).filter(Boolean)
  if (parts.length <= 1) {
    return <span className="font-mono text-[11px] text-slate-700 bg-slate-100/80 px-1.5 py-0.5 rounded border border-slate-200">{parts[0]}</span>
  }
  return (
    <div className="flex items-center gap-1.5 font-mono text-[11px] text-slate-700" title={rawIp}>
      <span className="bg-slate-100/80 px-1.5 py-0.5 rounded border border-slate-200">{parts[0]}</span>
      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-bold text-slate-500 border border-slate-200">
        +{parts.length - 1}
      </span>
    </div>
  )
}

export function Activity() {
  const [params, setParams] = useSearchParams()
  const [pollMs, setPollMs] = useState(0)
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const orgsQuery = useOrganizations({ pageSize: 100 })
  const orgOptions = [
    { value: '', label: 'All Organizations' },
    ...(orgsQuery.data?.data?.map((o) => ({ value: o.id, label: o.name })) || []),
  ]

  const filters = useMemo(
    () => ({
      action: params.get('action') ?? '',
      organization: params.get('organization') ?? '',
      search: params.get('search') ?? '',
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      page: Number(params.get('page') ?? '1'),
      pageSize: 25,
    }),
    [params],
  )

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== 'page') next.set('page', '1')
    setParams(next)
  }

  function handleSelectEvent(event: AuditEvent) {
    setSelectedEvent(event)
    setDetailOpen(true)
  }

  const activity = useActivity(filters, pollMs || false)

  const columns: Column<AuditEvent>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      className: 'w-[160px]',
      render: (row) => (
        <div>
          <div className="font-semibold text-slate-900 text-xs">{relativeTime(row.timestamp)}</div>
          <div className="font-mono text-[10px] text-slate-400">{exactTimestamp(row.timestamp)}</div>
        </div>
      ),
    },
    {
      key: 'document',
      header: 'Document',
      render: (row) => {
        const docTitle = row.documentTitle || row.document_title
        const docId = row.documentId || row.document_id
        const isExpired = (row.action || '').toLowerCase().includes('expire')

        return (
          <div className="max-w-[280px]">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-bold text-slate-900 text-xs truncate max-w-[220px]" title={docTitle || docId}>
                {docTitle || (docId ? `Doc #${docId.slice(-6)}` : 'System Event')}
              </span>
              {isExpired && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800 border border-amber-300">
                  EXPIRED
                </span>
              )}
            </div>
            {docId && (
              <div className="font-mono text-[10px] text-slate-400 mt-0.5 truncate max-w-[180px]">
                ID: {docId}
              </div>
            )}
          </div>
        )
      },
    },
    {
      key: 'organization',
      header: 'Organization',
      className: 'w-[180px]',
      render: (row) => {
        const orgName = row.orgName || row.org_name
        const orgId = row.orgId || row.org_id
        return (
          <div className="truncate max-w-[170px]" title={orgName || orgId}>
            <span className="font-semibold text-slate-800 text-xs">
              {orgName || (orgId ? `Org #${orgId.slice(-6)}` : 'Platform')}
            </span>
          </div>
        )
      },
    },
    {
      key: 'action',
      header: 'Action',
      className: 'w-[150px]',
      render: (row) => {
        const tag = formatActionTag(row.action)
        return (
          <div className="max-w-[140px]">
            <span className={`border px-2.5 py-1 rounded-md text-[11px] font-mono inline-flex items-center gap-1 shadow-2xs ${tag.style}`}>
              {tag.label}
            </span>
            {tag.detail && tag.detail.toLowerCase() !== tag.label.toLowerCase() && (
              <div className="text-[10px] text-slate-500 truncate mt-1" title={tag.detail}>
                {tag.detail}
              </div>
            )}
          </div>
        )
      },
    },
    {
      key: 'actor',
      header: 'Actor / Signer',
      className: 'w-[150px]',
      render: (row) => {
        const actor = getEffectiveActor(row)
        const isSystem = actor.name.toLowerCase() === 'system'
        return (
          <div className="truncate max-w-[140px]">
            <div className={`font-semibold text-xs truncate ${isSystem ? 'text-slate-500' : 'text-slate-900'}`}>
              {actor.name}
            </div>
            {actor.email && (
              <div className="font-mono text-[10px] text-slate-400 truncate" title={actor.email}>
                {actor.email}
              </div>
            )}
          </div>
        )
      },
    },
    {
      key: 'ip',
      header: 'IP Address',
      className: 'w-[140px]',
      render: (row) => formatIPs(row.ipAddress || row.ip_address, row.actor || row.actorName || row.actor_name),
    },
  ]

  return (
    <div className="space-y-4">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight">Activity Feed</h1>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            Real-time audit log of document operations, expiration triggers, and signing transactions.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SelectFilter
            label="Live Feed"
            value={String(pollMs)}
            options={POLL_OPTIONS}
            onChange={(v) => setPollMs(Number(v))}
          />
        </div>
      </div>

      {/* Quick Filter Presets */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1">Quick Presets:</span>
        <button
          onClick={() => updateParam('action', '')}
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${!filters.action
              ? 'bg-sky-600 text-white shadow-xs'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
        >
          All Activity
        </button>
        <button
          onClick={() => updateParam('action', 'expired')}
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${filters.action === 'expired'
              ? 'bg-amber-600 text-white shadow-xs'
              : 'border border-amber-200 bg-amber-50/70 text-amber-800 hover:bg-amber-100'
            }`}
        >
          Expired Documents
        </button>
        <button
          onClick={() => updateParam('action', 'signed')}
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${filters.action === 'signed'
              ? 'bg-emerald-600 text-white shadow-xs'
              : 'border border-emerald-200 bg-emerald-50/70 text-emerald-800 hover:bg-emerald-100'
            }`}
        >
          Signatures
        </button>
        <button
          onClick={() => updateParam('action', 'voided')}
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${filters.action === 'voided'
              ? 'bg-rose-600 text-white shadow-xs'
              : 'border border-rose-200 bg-rose-50/70 text-rose-800 hover:bg-rose-100'
            }`}
        >
          Voided &amp; Declined
        </button>
      </div>

      {/* Filter Panel */}
      <FilterPanel>
        <div className="min-w-[240px] flex-1">
          <span className="mb-1 block text-[10.5px] font-bold text-slate-500 uppercase">Search Feed</span>
          <SearchInput
            value={filters.search}
            onChange={(v) => updateParam('search', v)}
            placeholder="Document title, org name, email, ID..."
          />
        </div>
        <SelectFilter label="Filter Action" value={filters.action} options={ACTION_OPTIONS} onChange={(v) => updateParam('action', v)} />
        <SelectFilter label="Organization" value={filters.organization} options={orgOptions} onChange={(v) => updateParam('organization', v)} />
        <DateRangePicker
          from={filters.from}
          to={filters.to}
          onChange={(from, to) => {
            const next = new URLSearchParams(params)
            if (from) next.set('from', from)
            else next.delete('from')
            if (to) next.set('to', to)
            else next.delete('to')
            next.set('page', '1')
            setParams(next)
          }}
        />
        {(filters.search || filters.action || filters.organization || filters.from || filters.to) && (
          <button
            onClick={() => setParams(new URLSearchParams())}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-xs"
          >
            Reset Filters
          </button>
        )}
      </FilterPanel>

      {/* Data Table */}
      <DataTable
        columns={columns}
        rows={activity.data?.data ?? []}
        getRowId={(row) => row.id || Math.random().toString()}
        isLoading={activity.isLoading}
        isError={activity.isError}
        errorMessage="Could not load the activity feed."
        onRetry={() => activity.refetch()}
        emptyTitle="No matching activity events found"
        emptyDescription="Try clearing filters or adjusting your date range."
        onRowClick={(row) => handleSelectEvent(row)}
        page={filters.page}
        totalPages={activity.data?.meta.totalPages ?? 1}
        totalItems={activity.data?.meta.totalItems ?? 0}
        onPageChange={(p) => updateParam('page', String(p))}
      />

      {/* Rich Document Detail & Audit Drawer */}
      <DocumentDetailDrawer
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false)
          setSelectedEvent(null)
        }}
        event={selectedEvent}
        documentId={selectedEvent?.documentId || selectedEvent?.document_id}
      />
    </div>
  )
}
